import { useEventUploadEventPicture } from "@crowdcoursing/api/openapi";
import { useCallback, useState } from "react";

import { makeEventAvatarUrl } from "~utils/url";

interface UseEventAvatarResult {
  imageUrl: string | undefined;
  status: "idle" | "loading" | "error" | "success";
  uploadImage: (file: File) => Promise<void>;
}

export const useEventAvatar = (eventId: string | undefined): UseEventAvatarResult => {
  const [status, setStatus] = useState<UseEventAvatarResult["status"]>("idle");
  const [uploadTimestamp, setUploadTimestamp] = useState<number>();

  const { mutateAsync: uploadEventPicture } = useEventUploadEventPicture();

  // Get the avatar URL with proper URL construction
  const baseImageUrl = eventId ? makeEventAvatarUrl(eventId) : undefined;
  const imageUrl = baseImageUrl && uploadTimestamp ? `${baseImageUrl}?t=${uploadTimestamp}` : baseImageUrl;

  const uploadImage = useCallback(
    async (file: File) => {
      if (!eventId) {
        setStatus("error");
        throw new Error("Cannot upload image without an event ID");
      }

      setStatus("loading");

      // Create a new FormData instance
      const formData = new FormData();
      formData.append("FormFile", file);

      await uploadEventPicture(
        {
          // Cast FormData to the expected request body type
          body: formData as unknown as { FormFile?: Blob },
          headers: {
            // Remove Content-Type header to let the browser set it with the boundary
            "Content-Type": undefined,
          },
          pathParams: { eventId },
        },
        {
          onError: () => {
            setStatus("error");
          },
          onSuccess: () => {
            // Update timestamp to force a refresh of the image
            setUploadTimestamp(Date.now());
            setStatus("success");
          },
        },
      );
    },
    [eventId, uploadEventPicture],
  );

  return {
    imageUrl,
    status,
    uploadImage,
  };
};
